import { Stack, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { Sparkline } from '@repletoryreach/sdk-ui/charts';
import { BarList } from './BarList';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { topicByType, topicStats, type TopicStat } from '../lib/data';
import { MUTED, Muted, Panel, SeeChats, pct } from '../shared';
import type { TabProps } from './tab';

/**
 * What customers wrote about, in the business's own topics: how many chats each, its share of the chats read, how
 * many were fully answered and handed to the team, and how it went day by day; then each topic by kind of customer
 * ("how many parents asked about fees").
 */
export function Topics({ report, rows, filters, tz, onSee }: TabProps) {
  const t = useT();
  const names = (report.taxonomy?.topics ?? []).map((x) => x.name);
  const stats = topicStats(rows, names, filters.from, filters.to, tz);
  const describe = new Map((report.taxonomy?.topics ?? []).map((x) => [x.name, x.description]));
  const matrix = topicByType(rows, stats.map((s) => s.name));

  if (stats.length === 0) return <Muted>{t('insights.empty.topics')}</Muted>;
  return (
    <Stack gap="lg">
      <Panel title={t('insights.topics.title')} note={report.sampled ? t('insights.topics.sampled') : t('insights.topics.note')}>
        <BarList label={t('insights.topics.title')} items={stats.slice(0, 15).map((s) => ({ name: s.name, value: s.chats }))} />
      </Panel>

      <Panel title={t('insights.topics.table')} testId="insights-topics">
        <div className="rr-insights__table-scroll">
          <DataTable<Indexed<TopicStat>>
            records={stats as Indexed<TopicStat>[]}
            idAccessor="name"
            columns={[
              { accessor: 'name', title: t('insights.topics.topic'), render: (s) => (
                <Stack gap={0}>
                  <Text role="bodySmall" fw={600}>{s.name}</Text>
                  {describe.get(s.name) && <Text role="caption" c={MUTED}>{describe.get(s.name)}</Text>}
                </Stack>
              ) },
              { accessor: 'chats', title: t('insights.topics.chats'), textAlign: 'right', render: (s) => formatCount(s.chats) },
              { accessor: 'share', title: t('insights.topics.share'), textAlign: 'right', render: (s) => `${s.share}%` },
              { accessor: 'answered', title: t('insights.topics.answered'), textAlign: 'right', render: (s) => `${pct(s.answered, s.chats)}%` },
              { accessor: 'handedOver', title: t('insights.topics.handedOver'), textAlign: 'right', render: (s) => formatCount(s.handedOver) },
              { accessor: 'trend', title: t('insights.topics.trend'), render: (s) => (
                <span aria-label={t('insights.topics.trendAria', { name: s.name })}><Sparkline data={s.trend} w={96} h={28} withArea={false} /></span>
              ) },
              { accessor: 'refs', title: '', render: (s) => <SeeChats refs={s.refs} onSee={onSee} t={t} /> }
            ]}
          />
        </div>
      </Panel>

      {matrix.types.length > 0 && (
        <Panel title={t('insights.topics.byType')} note={t('insights.topics.byTypeNote')}>
          <div className="rr-insights__table-scroll">
            <table className="rr-insights__matrix" data-testid="insights-topic-types">
              <thead>
                <tr>
                  <th scope="col">{t('insights.topics.topic')}</th>
                  {matrix.types.map((ty) => <th key={ty} scope="col">{ty}</th>)}
                </tr>
              </thead>
              <tbody>
                {stats.map((s) => (
                  <tr key={s.name}>
                    <th scope="row">{s.name}</th>
                    {matrix.types.map((ty) => <td key={ty}>{formatCount(matrix.cells[s.name]?.[ty] ?? 0)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </Stack>
  );
}
